import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserContext } from "../../../providers/UserProvider";
import { useProducts } from "../../../hooks/useProducts/useProducts";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { useForm, useFormState } from "react-hook-form";
import { IOrganizationProductInstance } from "../../../domain/OrganizationProduct";
import { ATTRIBUTE_ROLES, NOT_SET, REFETCH_PERMISSIONS_EVENT_NAME } from "../../../constants/constants";
import { Attention, Button, Typography, Checkbox, Icon, Poptip } from "@optimizely/axiom";
import { UserSearchSelector } from "../UserSearchSelector/UserSearchSelector";
import { IInstanceFormValues } from "../InstanceForm/IInstanceFormValues";
import LimitByRole from "../LimitByRole/LimitByRole";
import { OptiIDEnabledCheckbox } from "../OptiIDEnabledCheckbox/OptiIDEnabledCheckbox";
import { emitToast } from "../../../lib/toaster-utils";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { useOpalBranding } from "../../../hooks/useOpalBranding/useOpalBranding";
import { Flags } from "../../../feature-flags/flags";
import styles from "./AccessManagementInstanceDetails.module.scss";
import {
    ANALYTICS_EVENT_NAMES,
    ANALYTICS_TRACKED_COMPONENTS,
    ANALYTICS_FLOWS
} from "../../../constants/analytics-constants";
import { useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { useOrganizationSettings } from "../../../hooks/useOrganizationSettings/useOrganizationSettings";
import { useOrganization } from "../../../hooks/useOrganization/useOrganization";

export const AccessManagementInstanceDetails = () => {
    const { organizationId } = useUserContext();
    const { products, isLoading, error, updateInstance } = useProducts({ organizationId });
    const { instanceId, productId } = useParams();
    const { enabled: enableCustomNavigationUrl, variables } = useFeatureFlag(Flags.ENABLE_CUSTOM_NAVIGATION_URL);
    const allowedCustomNavigationUrl = variables.enabled_products as { [key: string]: boolean };
    const enabledCustomNavigationUrlProducts = Object.keys(allowedCustomNavigationUrl).filter(
        (id) => allowedCustomNavigationUrl[id]
    );

    // Instance GenAI feature flag and dependencies
    const { enabled: enableInstanceGenAIFlag } = useFeatureFlag(Flags.ENABLE_OPAL_AUTO_PROVISIONING);
    const { opalName } = useOpalBranding();
    const { settings: orgSettings } = useOrganizationSettings({ organizationId });
    const { organization } = useOrganization({ id: organizationId });

    const { sendTrackEvent } = useAnalyticsTracking();

    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [optiEnabled, setOptiEnabled] = useState(false);
    const [showOptiWarning, setShowOptiWarning] = useState(false);

    const instance = products?.flatMap((p) => p.instances).find((inst) => inst.id === instanceId);
    const {
        optiIdEnabled,
        nickname,
        navigationUrl,
        name,
        technicalContactName,
        technicalContactEmail,
        id,
        genAIEnabled = false
    } = instance || {};

    // Instance GenAI state
    const [instanceGenAiEnabled, setInstanceGenAiEnabled] = useState(genAIEnabled);
    const [showInstanceGenAiDisableConfirmation, setShowInstanceGenAiDisableConfirmation] = useState(false);

    useEffect(() => {
        setInstanceGenAiEnabled(!!genAIEnabled);
    }, [genAIEnabled]);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        control,
        reset: resetFormState
    } = useForm<IInstanceFormValues>({
        mode: "onChange",
        defaultValues: {
            nickname: instance?.nickname,
            navigationUrl: instance?.navigationUrl
        }
    });

    const { isDirty } = useFormState({
        control
    });

    const [technicalContact, setTechnicalContact] = useState({
        email: instance?.technicalContactEmail,
        name: instance?.technicalContactName
    });

    const onEditCancel = () => {
        // revert technical contact changes
        setTechnicalContact({
            email: instance?.technicalContactEmail,
            name: instance?.technicalContactName
        });
        // revert checkbox state
        setInstanceGenAiEnabled(!!genAIEnabled);
        setEditing(false);

        resetFormState();
    };
    // Instance GenAI eligibility check
    const organizationGenAIEnabled = orgSettings?.genAI?.enabled;
    const isHIPAAOrg = organization?.healthCareDataEnabled;
    const isGenAIEligible = useMemo(
        () => enableInstanceGenAIFlag && organizationGenAIEnabled && !isHIPAAOrg && optiIdEnabled,
        [enableInstanceGenAIFlag, organizationGenAIEnabled, isHIPAAOrg, optiIdEnabled]
    );
    // Build tooltip content for ineligible instances
    const buildGenAITooltipContent = useCallback(() => {
        if (isGenAIEligible) return "";

        const reasons = [];
        if (!organizationGenAIEnabled) reasons.push(<p>Your organization has {opalName} disabled</p>);
        if (isHIPAAOrg) reasons.push(<p>Your organization is flagged as HIPAA compliant</p>);
        if (!enableInstanceGenAIFlag) reasons.push(<p>{opalName} is not yet provisioned</p>);
        if (!optiIdEnabled) reasons.push(<p>The instance is not Opti ID enabled</p>);

        return (
            <Typography type="caption" className={styles["access-instance-details-genAi-poptip"]}>
                <p>This instance is not eligible for {opalName} because of the following:</p>
                <ul>
                    {reasons.map((reason, index) => (
                        <li key={index}>{reason}</li>
                    ))}
                </ul>
            </Typography>
        );
    }, [enableInstanceGenAIFlag, isGenAIEligible, isHIPAAOrg, opalName, optiIdEnabled, organizationGenAIEnabled]);

    const genAITooltipContent = useMemo(() => buildGenAITooltipContent(), [buildGenAITooltipContent]);

    if (isLoading) return <LoadingIndicator />;
    if (error) return <Typography type="body">Error fetching instance details.</Typography>;
    if (!instance) return <Typography type="body">Error: instance not found in organization.</Typography>;

    const toggleOptiEnabled = () => {
        if (!optiEnabled) {
            setShowOptiWarning(true);
        }
    };

    const toggleInstanceGenAiEnabled = () => {
        if (genAIEnabled && instanceGenAiEnabled) {
            // If currently enabled and trying to disable, show confirmation
            setShowInstanceGenAiDisableConfirmation(true);
        } else {
            // If currently disabled and trying to enable, toggle directly
            setInstanceGenAiEnabled(!instanceGenAiEnabled);
        }
    };

    const handleInstanceGenAiDisableCancel = () => {
        setShowInstanceGenAiDisableConfirmation(false);
    };

    const handleInstanceGenAiDisableConfirm = () => {
        setInstanceGenAiEnabled(false);
        setShowInstanceGenAiDisableConfirmation(false);
    };

    const handleFormSubmission = async () => {
        if (!editing) {
            setEditing(true);
            setValue("nickname", instance?.nickname || instance?.name || "");
            setValue("navigationUrl", instance?.navigationUrl || "");
            return;
        }

        if (!instance) return;

        setLoading(true);
        const { nickname, navigationUrl } = getValues();
        const updatedInstance: IOrganizationProductInstance = {
            ...instance,
            nickname,
            navigationUrl: navigationUrl || undefined,
            technicalContactEmail: technicalContact.email || instance.technicalContactEmail,
            technicalContactName: technicalContact.name || instance.technicalContactName
        };

        updatedInstance["optiIdEnabled"] = !!(optiIdEnabled || optiEnabled);

        if (enableInstanceGenAIFlag) {
            updatedInstance["genAIEnabled"] = instanceGenAiEnabled;
        }

        return updateInstance({ instance: updatedInstance, previousInstance: instance })
            ?.then(() => {
                setEditing(false);
                emitToast({ message: "This instance has been successfully updated." });
                sendTrackEvent({
                    name: ANALYTICS_EVENT_NAMES.INSTANCE_DETAILS_UPDATED,
                    component: ANALYTICS_TRACKED_COMPONENTS.INSTANCE_DETAILS_PAGE,
                    flow: ANALYTICS_FLOWS.PRODUCT_ACCESS,
                    data: {
                        instanceId: instance?.id,
                        instanceName: instance?.nickname || instance?.name
                    }
                });
                window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
            })
            .catch((e: any) => {
                console.error(e);
                setErrorMessage("An error occurred. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    let actionText = editing ? "Save" : "Edit";

    const formDirty =
        isDirty ||
        (technicalContact.email && technicalContact.email !== instance?.technicalContactEmail) ||
        optiEnabled ||
        instanceGenAiEnabled !== genAIEnabled;

    const bothTechnicalContactFields = technicalContactEmail && technicalContactName;
    const contact = technicalContactName || technicalContactEmail || "None";
    const disableEditing = false;

    let warningText = (
        <Typography type="body">
            Enabling Opti ID can't be reversed. Please ensure all prerequisites are met and the instance has been set up
            on Opti ID. Do you want to proceed?
            <Attention alignment="left" className="push-double--top push-double--bottom" type="warning">
                {opalName} may change if you enable Opti ID, depending on the product and your organization settings.
            </Attention>
        </Typography>
    );

    if (process.env.REACT_APP_CMS_PRODUCT_ID === productId) {
        warningText = (
            <Typography type="body">
                <div>Enabling Opti ID can't be reversed. Do you want to proceed?</div>
                <div className="push-double--top">
                    To enable Opti ID in your CMS application please follow{" "}
                    <a
                        className="push-double--top"
                        href="https://support.optimizely.com/hc/en-us/articles/18127857412493-Configure-Opti-ID-for-CMS"
                    >
                        these instructions
                    </a>{" "}
                    and update your application code and do a new deploy.
                </div>
                <div className="push-double--top">Do not proceed if all steps have not been completed.</div>
            </Typography>
        );
    } else if (process.env.REACT_APP_B2B_PRODUCT_ID === productId) {
        warningText = (
            <Typography type="body">
                <div>Enabling Opti ID can't be reversed. Do you want to proceed?</div>
                <div className="push-double--top">
                    Optimizely Configured Commerce must be 5.2.2403 or later to use Opti ID. If not upgraded, please
                    cancel enabling Opti ID.
                </div>
            </Typography>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit(handleFormSubmission)} className={styles["access-instance-details-form"]}>
                <div className="push-double--sides ">
                    {!!errorMessage && (
                        <Attention
                            alignment="left"
                            className="push-triple--top"
                            type="bad-news"
                            onDismiss={() => setErrorMessage("")}
                        >
                            {errorMessage}
                        </Attention>
                    )}
                    <label className="oui-label push-double--top" htmlFor="role-description">
                        Name
                    </label>
                    {editing ? (
                        <input
                            aria-describedby="display-name"
                            className="oui-text-input"
                            placeholder="Display Name"
                            id="display-name"
                            disabled={loading}
                            type="text"
                            {...register("nickname")}
                        />
                    ) : (
                        <Typography type="body" className="label--disabled">
                            {nickname || name}
                        </Typography>
                    )}

                    {enableCustomNavigationUrl && (
                        <>
                            <label className="oui-label push-double--top" htmlFor="navigation-url">
                                Navigation URL
                            </label>
                            {editing && enabledCustomNavigationUrlProducts.includes(productId as string) ? (
                                <input
                                    aria-describedby="navigation-url"
                                    className="oui-text-input"
                                    placeholder="Enter a fully qualified URL for the instance"
                                    id="navigation-url"
                                    disabled={loading}
                                    type="text"
                                    {...register("navigationUrl")}
                                />
                            ) : (
                                <Typography type="body" className="label--disabled">
                                    {navigationUrl || NOT_SET}
                                </Typography>
                            )}
                        </>
                    )}

                    <label className="oui-label push-double--top" htmlFor="technical-contact-name">
                        Technical Contact
                    </label>
                    {editing ? (
                        <UserSearchSelector
                            placeholder={technicalContact.email || technicalContactEmail || "Email"}
                            value={technicalContact.email || technicalContactEmail || ""}
                            onSelectUser={(user) => {
                                if (user) {
                                    setTechnicalContact({
                                        email: user.email,
                                        name: `${user.firstName} ${user.lastName}`
                                    });
                                }
                            }}
                            isUserDisabled={(user) => {
                                return user.email === technicalContactEmail;
                            }}
                        />
                    ) : (
                        <>
                            <Typography type="body" className="label--disabled">
                                {bothTechnicalContactFields ? (
                                    <>
                                        {technicalContactName} <br />
                                        {technicalContactEmail}
                                    </>
                                ) : (
                                    <>{contact}</>
                                )}
                            </Typography>
                        </>
                    )}
                    <div className="push-double--top">
                        <OptiIDEnabledCheckbox
                            checked={optiIdEnabled || optiEnabled}
                            disabled={optiIdEnabled}
                            editing={editing}
                            onChange={toggleOptiEnabled}
                        />
                    </div>

                    {enableInstanceGenAIFlag && (
                        <div className="push-double--top">
                            <div className="flex align-items-center">
                                <Checkbox
                                    checked={instanceGenAiEnabled}
                                    isDisabled={!editing || !isGenAIEligible || loading}
                                    onChange={toggleInstanceGenAiEnabled}
                                    label={`${opalName} Enabled`}
                                    data-testid="instance-genai-checkbox"
                                />
                                {!isGenAIEligible && editing && (
                                    <Poptip
                                        isInline
                                        trigger="mouseenter"
                                        content={genAITooltipContent}
                                        className="push--left"
                                    >
                                        <Icon name="circle-info" size="small" data-testid="genai-eligibility-tooltip" />
                                    </Poptip>
                                )}
                            </div>
                        </div>
                    )}

                    <label className="oui-label push-double--top" htmlFor="instance-id">
                        Id
                    </label>
                    <Typography type="body" className="label--disabled">
                        {id}
                    </Typography>
                </div>
                <div className="push-double--sides push-double--top">
                    <LimitByRole action={ATTRIBUTE_ROLES.USERS.UPDATE} mode="disable">
                        <Button
                            key="edit-instance"
                            className="push--right"
                            // eslint-disable-next-line react/style-prop-object
                            style="highlight"
                            isLoading={editing && loading}
                            isDisabled={disableEditing || loading || (editing && !formDirty)}
                            loadingText="Saving"
                            isSubmit
                        >
                            {actionText}
                        </Button>
                        {editing && (
                            // eslint-disable-next-line react/style-prop-object
                            <Button
                                key={0}
                                isDisabled={loading}
                                // eslint-disable-next-line react/style-prop-object
                                style="outline-reverse"
                                onClick={onEditCancel}
                            >
                                Cancel
                            </Button>
                        )}
                    </LimitByRole>
                </div>
            </form>
            {showOptiWarning && (
                <ConfirmationDialog
                    callToActionText="Proceed"
                    onCancel={() => {
                        setShowOptiWarning(false);
                    }}
                    onConfirmation={() => {
                        setOptiEnabled(true);
                        setShowOptiWarning(false);
                    }}
                    title="Warning"
                >
                    {warningText}
                </ConfirmationDialog>
            )}
            {showInstanceGenAiDisableConfirmation && (
                <ConfirmationDialog
                    onCancel={handleInstanceGenAiDisableCancel}
                    onConfirmation={handleInstanceGenAiDisableConfirm}
                    title={`Disable ${opalName}`}
                    callToActionText="Confirm"
                >
                    <div>
                        Turning off {opalName} will disable AI-powered features for this instance, including {opalName}{" "}
                        role suggestions when granting access.
                    </div>
                </ConfirmationDialog>
            )}
        </>
    );
};

AccessManagementInstanceDetails.displayName = "AccessManagementInstanceDetails";
